import { CoverageOption, PaymentPlan, Policy } from "@deathstar/types/northstar";
import { DateTime, NumberFormatter } from "@deathstar/types/util";
import { Tooltip } from "@material-ui/core";
import React from "react";
import { classNames } from "../classNames/classNames";
import { Collapse } from "../collapse/collapse";
import { PolicyInsuranceCompanies } from "../policyInsuranceCompanies/policyInsuranceCompanies";
import { PolicyStatus } from "../policyStatus/policyStatus";
import { PolicyTerm } from "../policyTerm/policyTerm";
import { BillingSettings } from "./billingSettings";
import { Context } from "./context";

export function DialogHeader({ policy }: { policy: Policy }): JSX.Element | null {
    const { billing } = policy;
    if (!billing) return null;

    return (
        <div className="flex flex-col">
            <PolicyDescription policy={policy} />
            <div className="grid grid-cols-[max-content,1fr] justify-between gap-4">
                <PolicySummary policy={policy} />
                <AccountingSummary policy={policy} />
            </div>
        </div>
    );
}

function PolicySummary({ policy }: { policy: Policy }): JSX.Element {
    const { waypointVersion } = React.useContext(Context);

    return (
        <div>
            <ul className="flex flex-col gap-1">
                <li className="flex gap-2">
                    <PolicyInsuranceCompanies policy={policy} />
                </li>
                <li className="flex gap-2">
                    {/* <CalendarIcon className="h-5 w-5 text-orange-400" /> */}
                    <div className="flex items-end gap-2">
                        <PolicyTerm policy={policy} />
                        <PolicyStatus policy={policy} />
                    </div>
                </li>
                {!waypointVersion && (
                    <>
                        <li>
                            <PaymentPlanName policy={policy} />
                        </li>
                        <li>
                            <BillMethod policy={policy} />
                        </li>
                    </>
                )}
            </ul>
        </div>
    );
}

function AccountingSummary({ policy }: { policy: Policy }): JSX.Element | null {
    const { waypointVersion, enableActions } = React.useContext(Context);
    const [showMore, setShowMore] = React.useState(false);

    if (waypointVersion && (enableActions === true || (typeof enableActions === "object" && enableActions.billingSettings))) {
        return <BillingSettings billingSettings={policy.billingSettings} policy={policy} />;
    }
    if (waypointVersion) return null;

    const billing = policy.billing!;

    return (
        <>
            <div className="justify-self-end">
                <table className="w-full table-auto border-separate border-spacing-x-4 border-spacing-y-1 pb-2">
                    <tbody className="">
                        <tr>
                            <Tooltip title="Cancellation must be initiated by this date in order to avoid financial exposure. Any escrow and/or down payment is included in this calculation.">
                                <td className="text-right">Agency Exposure Date</td>
                            </Tooltip>
                            <td>
                                <AgencyExposurePill policy={policy} />
                            </td>
                        </tr>
                        <tr>
                            <td className="text-right">Runway Remaining</td>
                            <td>
                                <RunwayRemainingPill policy={policy} />
                            </td>
                        </tr>
                        <tr>
                            <td className="text-right">Open Balance</td>
                            <td>
                                <OpenBalancePill policy={policy} />
                            </td>
                        </tr>
                        <tr>
                            <td className="text-right">Total Projected Cost</td>
                            <TotalProjectedCostPill policy={policy} />
                        </tr>
                        <tr>
                            <td className="text-right">Total Projected Commission</td>
                            <TotalProjectedCommissionPill policy={policy} />
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="col-span-2">
                <Collapse isOpen={showMore}>
                    {/* <hr className="pb-2" /> */}
                    <div className="grid grid-cols-[1fr_max-content] gap-4">
                        <div className="flex flex-col"></div>

                        <table className="w-full table-auto border-separate border-spacing-x-4 border-spacing-y-1">
                            <tbody>
                                <tr>
                                    <td className="text-right">Projected Cost Per Day</td>
                                    <td>
                                        <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                                            <span>{NumberFormatter.Currency.format(billing.projectedCostPerDay)}</span>
                                        </div>
                                    </td>
                                </tr>
                                {billing.specialPayment.amount > 0 && (
                                    <tr>
                                        <td className="text-right">
                                            {billing.specialPayment.isEscrowDeposit ? "Escrow Deposit" : "Down Payment"}
                                        </td>
                                        <td>
                                            <Tooltip
                                                title={NumberFormatter.Currency.format(billing.specialPayment.amount)}
                                                placement="right"
                                            >
                                                <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                                                    <span>{billing.specialPaymentRunway} days</span>
                                                </div>
                                            </Tooltip>
                                        </td>
                                    </tr>
                                )}
                                {billing.totalPremiumCollateralAmount > 0 && (
                                    <tr>
                                        <td className="text-right">Premium Collateral</td>
                                        <td>
                                            <Tooltip
                                                title={NumberFormatter.Currency.format(billing.totalPremiumCollateralAmount)}
                                                placement="right"
                                            >
                                                <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                                                    <span>{billing.specialPaymentRunway} days</span>
                                                </div>
                                            </Tooltip>
                                        </td>
                                    </tr>
                                )}
                                <DebugValues policy={policy} />
                            </tbody>
                        </table>
                    </div>
                </Collapse>
                <div className="mr-4 text-right">
                    <button
                        className="text-xs text-blue-600"
                        onClick={() => {
                            setShowMore(!showMore);
                        }}
                    >
                        show {showMore ? "less" : "more"}
                    </button>
                </div>
            </div>
        </>
    );
}

function PolicyDescription({ policy }: { policy: Policy }): JSX.Element | null {
    const { waypointVersion } = React.useContext(Context);
    if (waypointVersion) return null;

    return (
        <div className="text-xl">
            <div className="mb-2 border-b border-gray-200 pb-2">
                <div>
                    <PolicyDescriptionText policy={policy} />
                </div>
            </div>
        </div>
    );
}

function PolicyDescriptionText({ policy }: { policy: Policy }): JSX.Element {
    const coverages = React.useMemo(() => {
        if (policy.description.split(",").length > 2) {
            return Policy.getDescription(policy);
        }

        let coverageMetadata = policy.coverages?.map((c) => CoverageOption.getMetadata(c.coverageOptionId)) || [];
        if (!coverageMetadata.length && policy.description.length) {
            coverageMetadata = policy.description.split(",").map((desc) => {
                return CoverageOption.getMetadataFromAbbreviation(desc as unknown as CoverageOption.Abbreviation);
            });
        }
        return coverageMetadata.map((meta) => meta.name).join(", ");
    }, [policy]);

    return <p>{coverages}</p>;
}

function AgencyExposurePill({ policy }: { policy: Policy }): JSX.Element {
    const { billing } = policy;
    if (!billing) return <span className="px-4">--</span>;
    return (
        <div
            className={classNames("rounded-full border border-black px-4 text-center", {
                "border-danger text-danger":
                    !billing.isPaidOff &&
                    policy.billMethod !== Policy.BillMethod.DIRECT &&
                    DateTime.today.isAfter(billing.agencyExposureDate),
            })}
        >
            <AgencyExposureDate policy={policy} />
        </div>
    );
}
function AgencyExposureDate({ policy }: { policy: Policy }): JSX.Element {
    const { billing } = policy;
    if (!billing) return <span className="px-4">--</span>;

    if (billing.isPaidOff || policy.billMethod === Policy.BillMethod.DIRECT) {
        return <span className="px-4">--</span>;
    }
    return <span>{billing.agencyExposureDate.format("mm/dd/YYYY")}</span>;
}

function RunwayRemainingPill({ policy }: { policy: Policy }): JSX.Element {
    const { billing } = policy;
    if (!billing) return <span className="px-4">--</span>;

    return (
        <div
            className={classNames("rounded-full border border-black px-4 text-center", {
                "border-danger text-danger":
                    !billing.isPaidOff && policy.billMethod !== Policy.BillMethod.DIRECT && billing.daysUntilAgencyExposure <= 0,
            })}
        >
            <RunwayRemainingAmount policy={policy} />
        </div>
    );
}

function RunwayRemainingAmount({ policy }: { policy: Policy }): JSX.Element {
    const { billing } = policy;

    if (!billing || billing.isPaidOff || policy.billMethod === Policy.BillMethod.DIRECT) {
        return <span className="px-4">--</span>;
    }
    return <span>{NumberFormatter.Currency.format(billing.isPaidOff ? 0 : billing.exposureAmount)}</span>;
}

function OpenBalancePill({ policy }: { policy: Policy }): JSX.Element {
    const { billing } = policy;
    if (!billing) return <span className="px-4">--</span>;

    return (
        <Tooltip title="Includes Overdue" placement="right">
            <div
                className={classNames("rounded-full border border-black px-4 text-center", {
                    "border-danger text-danger": billing.totalOverdue > 0,
                    "border-blue-600 text-blue-600": billing.totalDueTodayAndOverdue > 0 && billing.totalOverdue === 0,
                })}
            >
                <OpenBalanceAmount policy={policy} />
            </div>
        </Tooltip>
    );
}

function TotalProjectedCostPill({ policy }: { policy: Policy }): JSX.Element {
    const { billing } = policy;
    if (!billing) return <span className="px-4">--</span>;

    return (
        <td>
            <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                <span>{NumberFormatter.Currency.format(billing.totalProjectedCost)}</span>
            </div>
        </td>
    );
}

function TotalProjectedCommissionPill({ policy }: { policy: Policy }): JSX.Element {
    const { billing } = policy;
    if (!billing) return <span className="px-4">--</span>;

    return (
        <td>
            <Tooltip
                placement="right"
                title={
                    <div>
                        {billing.commissionByLine?.size > 0 && (
                            <div>
                                {Array.from(billing.commissionByLine.values()).map((line, i) => (
                                    <p key={i}>
                                        {line.commissionOption?.code?.trim() || "All lines"}, {line.commissionOption.metadata?.rate}%
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>
                }
            >
                <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                    <span>{NumberFormatter.Currency.format(billing.totalProjectedCommission)}</span>
                </div>
            </Tooltip>
        </td>
    );
}

function OpenBalanceAmount({ policy }: { policy: Policy }): JSX.Element {
    const { billing } = policy;

    if (!billing || billing.isPaidOff || policy.billMethod === Policy.BillMethod.DIRECT) {
        return <span className="px-4">--</span>;
    }
    return <span>{NumberFormatter.Currency.format(billing.totalDueTodayAndOverdue > 0 ? billing.totalDueTodayAndOverdue : 0)}</span>;
}

function PaymentPlanName({ policy }: { policy: Policy }): JSX.Element {
    const planName = React.useMemo(() => {
        if (policy.paymentPlanId === PaymentPlan.Id.FULL) {
            return "Full Pay";
        }
        return PaymentPlan.getTypeById(policy.paymentPlanId);
    }, [policy.paymentPlanId]);
    if (planName === PaymentPlan.Type.COMPOSITE) {
        return (
            <p>
                {planName} (
                {Policy.isCompositeArrears(policy) ? "Arrears" : Policy.isCompositeDoubleAdvanced(policy) ? "Double Advanced" : "Advanced"})
            </p>
        );
    }
    return <p>{planName}</p>;
}

function BillMethod({ policy }: { policy: Policy }): JSX.Element {
    return (
        <div
            className={classNames("inline-block rounded-full border border-blue-200 bg-blue-50 px-2", {
                "border-purple-200 bg-purple-50/50 text-purple-700": policy.billMethod === Policy.BillMethod.DIRECT,
                "border-sky-200 bg-sky-50/50 text-sky-700": policy.billMethod === Policy.BillMethod.AGENCY,
            })}
        >
            <span>{policy.billMethod} Bill</span>
        </div>
    );
}

function DebugValues({ policy }: { policy: Policy }): JSX.Element | null {
    const { billing } = policy;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (!billing || !(window as any).DEBUG) return null;

    return (
        <>
            <tr>
                <td className="text-right">daysUntilAgencyExposure</td>
                <td>
                    <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                        <span>{billing.daysUntilAgencyExposure}</span>
                    </div>
                </td>
            </tr>
            <tr>
                <td className="text-right">datePaidThrough</td>
                <td>
                    <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                        <span>{billing.datePaidThrough.format("mm/dd/YYYY")}</span>
                    </div>
                </td>
            </tr>
            <tr>
                <td className="text-right">totalDaysPaidFor</td>
                <td>
                    <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                        <span>{billing.totalDaysPaidFor}</span>
                    </div>
                </td>
            </tr>
            <tr>
                <td className="text-right">daysUntilAgencyExposure</td>
                <td>
                    <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                        <span>{billing.daysUntilAgencyExposure}</span>
                    </div>
                </td>
            </tr>
            <tr>
                <td className="text-right">totalDaysIntoPolicy</td>
                <td>
                    <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                        <span>{billing.totalDaysIntoPolicy}</span>
                    </div>
                </td>
            </tr>
            <tr>
                <td className="text-right">totalDaysInPolicy</td>
                <td>
                    <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                        <span>{billing.totalDaysInPolicy}</span>
                    </div>
                </td>
            </tr>
            <tr>
                <td className="text-right">totalDaysIntoPolicyPaidWithoutSpecialPayment</td>
                <td>
                    <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                        <span>{billing.totalDaysIntoPolicyPaidWithoutSpecialPayment}</span>
                    </div>
                </td>
            </tr>
            <tr>
                <td className="text-right">totalPaid</td>
                <td>
                    <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                        <span>{NumberFormatter.Currency.format(billing.totalPaid)}</span>
                    </div>
                </td>
            </tr>
            <tr>
                <td className="text-right">totalDue</td>
                <td>
                    <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                        <span>{NumberFormatter.Currency.format(billing.totalDue)}</span>
                    </div>
                </td>
            </tr>
            <tr>
                <td className="text-right">totalOverdue</td>
                <td>
                    <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                        <span>{NumberFormatter.Currency.format(billing.totalOverdue)}</span>
                    </div>
                </td>
            </tr>
            <tr>
                <td className="text-right">total due and overdue</td>
                <td>
                    <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                        <span>{NumberFormatter.Currency.format(billing.totalDue + billing.totalOverdue)}</span>
                    </div>
                </td>
            </tr>
            <tr>
                <td className="text-right">totalPastOverdue</td>
                <td>
                    <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                        <span>{NumberFormatter.Currency.format(billing.totalPastOverdue)}</span>
                    </div>
                </td>
            </tr>
            <tr>
                <td className="text-right">totalUpcoming</td>
                <td>
                    <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                        <span>{NumberFormatter.Currency.format(billing.totalUpcoming)}</span>
                    </div>
                </td>
            </tr>
            <tr>
                <td className="text-right">totalCharged</td>
                <td>
                    <div className={classNames("rounded-full border border-black px-4 text-center", {})}>
                        <span>{NumberFormatter.Currency.format(billing.totalCharged)}</span>
                    </div>
                </td>
            </tr>
        </>
    );
}
